import React, { FC, useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { get } from 'lodash';
import { FormDataType } from 'contexts/FormProvider';
import { InformationPopUp } from 'components';
import { ContactPreferredMethod } from 'pages/common/components/ContactPreferredMethod';
import { FormGroupLabel, TextField, Dropdown, Button } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { phoneNumberSchema } from '../schema';
import ContactOptions from '../components/ContactOptions';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { FormSpacer } from 'components';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';
import { createFormErrorMessage } from 'utils/helperMethods';

interface Props {
  form: UseFormReturn<FormDataType>;
  show: boolean;
  showNextButton: boolean;
  isContactDetailsValid: boolean;
}

const ContactDetails: FC<Props> = (props) => {
  const { form, show, showNextButton = true, isContactDetailsValid = false } = props;

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const {
    register,
    watch,
    setValue,
    setError,
    trigger: triggerValidation,
    clearErrors: clearError,
    formState: { errors },
    control,
  } = form;

  const values: object = watch();
  const iContactPreferedMethod = watch('iContactPreferedMethod') || '';
  const mPhoneNumber = watch('mPhoneNumber') || '';
  const hPhoneNumber = watch('hPhoneNumber') || '';
  const oPhoneNumber = watch('oPhoneNumber') || '';
  const contactPreferredFields = ['cEmailAddress', 'mPhoneNumber', 'hPhoneNumber', 'oPhoneNumber'];

  useEffect(() => {
    const haveOneValidNumber = [mPhoneNumber, hPhoneNumber, oPhoneNumber].some((phoneNumber) =>
      phoneNumberSchema(false).isValidSync(phoneNumber)
    );

    setValue('haveOneValidNumber' as keyof FormDataType, haveOneValidNumber);

    if (iContactPreferedMethod === 'phone' && !haveOneValidNumber) {
      setError(
        'haveOneValidNumber' as keyof FormDataType,
        createFormErrorMessage('required', 'must have one valid number')
      );
    } else clearError('haveOneValidNumber' as keyof FormDataType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hPhoneNumber, mPhoneNumber, oPhoneNumber, iContactPreferedMethod]);

  useEffect(() => {
    register('iContactPreferedMethod');
    register('cEmailAddress');
    register('physicalAddress');
    register('physicalAddressObject');
    register('phyStreetAddress');
    register('phySuburb');
    register('phyCountry');
    register('phyState');
    register('phyPostcode');
    register('isPhysAddressValid');
    register('isManualPhysAddress');
    register('postalAddress');
    register('postalAddressObject');
    register('posStreetAddress');
    register('posSuburb');
    register('posCountry');
    register('posState');
    register('posPostcode');
    register('isPosAddressValid');
    register('isManualPosAddress');
    register('addressType');
    register('mPhoneNumber');
    register('hPhoneNumber');
    register('oPhoneNumber');
    register('isLangInterpreter');
    register('language');
    register('vulnerableCustomer');
    register('otherLanguage');
  }, [register]);

  const isComplaint = watch('lodgement') === 'Complaint';
  const isOtherLanguage = watch('language') === 'Other';
  const isLangInterpreter = watch('isLangInterpreter');

  if (!show || !isComplaint) return null;

  function handleClick(): void {
    setFormCompleteEvent();
  }

  // Localized content constants
  const sectionHeading = getTextContent('consent.contactDetails.heading');
  const preferedContactTypeTitle = getTextContent('consent.contactDetails.preferedContactType');
  const preferedContactTypeInfoText = getTextContent('consent.contactDetails.preferedContactType.infoText');
  const languageInterpreterTitle = getTextContent('insured.otherDetails.languageInterpreter');
  const languageTitle = getTextContent('insured.otherDetails.language');
  const otherLanguageTitle = getTextContent('insured.otherDetails.otherLanguage');

  return (
    <>
      <FormCard variant="sectionTitle" title={sectionHeading}>
        <ContactPreferredMethod
          title={preferedContactTypeTitle}
          titleSuffix={<InformationPopUp message={preferedContactTypeInfoText} />}
          name={'iContactPreferedMethod'}
          active={iContactPreferedMethod}
          onChange={(method: string, fields) => {
            setValue('iContactPreferedMethod', method);

            fields.forEach((field: string) => {
              if (get(values, field) && get(values, field).length > 0) {
                triggerValidation(field as keyof FormDataType);
                setFormStartEvent();
              } else {
                clearError(field as keyof FormDataType);
              }

              return field;
            });
          }}
        />
        <FormSpacer type="input" />
        <ContactOptions form={form} />

        <Controller
          name="isLangInterpreter"
          control={control}
          rules={{ required: '' }}
          render={({ field }) => (
            <Toggle
              {...field}
              title={languageInterpreterTitle}
              data-track="isLangInterpreter"
              data-track-value="capture"
              required
              optionList={[
                {
                  autoComplete: 'off',
                  id: 'isLangInterpreter_yes',
                  label: 'Yes',
                  value: 'yes',
                  defaultChecked: 'yes' === isLangInterpreter,
                },
                {
                  autoComplete: 'off',
                  id: 'isLangInterpreter_no',
                  label: 'No',
                  value: 'no',
                  defaultChecked: 'no' === isLangInterpreter,
                },
              ]}
              onChange={(e) => {
                const value = e.target.value;
                field.onChange(value);
                setValue('isLangInterpreter', value);

                if (value === 'yes') {
                  setValue('vulnerableCustomer', 'yes');
                }
                triggerValidation('isLangInterpreter');
              }}
            />
          )}
        />

        {isLangInterpreter === 'yes' && (
          <>
            <FormGroupLabel required title={languageTitle} alignCenter />
            <FormSpacer type="input" />
            <Dropdown
              sx={{ maxWidth: 400 }}
              id="language"
              label="language"
              title={getTextContent(`insured.otherDetails.language`)}
              optionList={[
                'Arabic',
                'Cantonese',
                'Greek',
                'Hindi',
                'Italian',
                'Mandarin',
                'Punjabi',
                'Spanish',
                'Vietnamese',
                'Other',
              ].map((value) => ({ value, label: value }))}
              data-track="language"
              data-track-value="capture"
              value={watch('language') || ''}
              multiple={false}
              helperText=""
              onChange={(event) => {
                const value = event.target.value as string;
                setValue('language', value);
                triggerValidation('language');
              }}
              onClose={() => triggerValidation('language')}
            />
            <FormSpacer type="input" />
          </>
        )}

        {isOtherLanguage && isLangInterpreter === 'yes' && (
          <>
            <FormGroupLabel title={otherLanguageTitle} alignCenter />
            <FormSpacer type="input" />
            <Controller
              control={control}
              name="otherLanguage"
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="otherLanguage"
                  error={!!errors.otherLanguage}
                  label="Other language"
                  value={field.value as string | undefined}
                  data-track="otherLanguage"
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 50,
                    minLength: 3,
                  }}
                  data-test="otherLanguage"
                  helperText={errors.otherLanguage ? errors.otherLanguage?.message : null}
                />
              )}
            />
            <FormSpacer type="input" />
          </>
        )}

        {showNextButton && (
          <>
            <FormSpacer type="input" />
            <Button type="submit" onClick={handleClick} label="Continue" disabled={!isContactDetailsValid} />
          </>
        )}
      </FormCard>
    </>
  );
};

export default ContactDetails;
